import { normalize, round } from "../../../utils/typeUtils"
import { Tax, TaxBase, TaxType, VatTaxCode } from "../state/types"
import { Labels } from "../../../features/data-types"
import { DataType } from "../../../tables"
import { useFiltersHooks } from "../../../features/ui"
import { useAdministrationState } from "./administrationHooks"
import { usePaymentOrderLoaders } from "../loaders/paymentOrderLoaders"
import { useTaxLoaders } from "../loaders/taxLoaders"
import { useProductPricesLoaders } from "../../products"
import { useTicketLoaders } from "../loaders/ticketLoaders"

export const useTaxHooks = () => {
    const { taxes } = useAdministrationState()
    const { search } = useFiltersHooks()

    const taxLoaders = useTaxLoaders()
    const productPricesLoaders = useProductPricesLoaders()
    const paymentOrderLoaders = usePaymentOrderLoaders()
    const ticketLoaders = useTicketLoaders()

    const getSaleTaxes = () => taxes.filter(tax => tax.type === TaxType.SALE_TAX)

    const getTotalSaleTax = () => getSaleTaxes().reduce((sum, tax) => sum + tax.yield, 0)

    const getVat21Tax = () => taxLoaders().findTaxByCode(VatTaxCode.VAT_21)!
    
    const calculateTaxBaseAmount = (
        tax: Tax,
        total: number,
        vatAmount: number
    ) => {
        return tax.taxBase === TaxBase.PRICE_WITH_VAT ? total : (tax.taxBase === TaxBase.VAT ? vatAmount : total - vatAmount)
    }
    
    const calculateTaxAmount = (
        tax: Tax,
        total: number,
        vatAmount: number
    ) => {
        return round(calculateTaxBaseAmount(tax, total, vatAmount) * tax.yield / 100)
    }

    const canRemoveTax = async (taxId: string) => {
        const { loadTax } = taxLoaders()
        const tax = await loadTax(taxId, true)

        const { countProductPrices } = productPricesLoaders()
        const productPricesCount = await countProductPrices('vatTaxId', taxId)

        const { loadPaymentOrdersWithProp } = paymentOrderLoaders()
        const paymentOrdersWithTaxItems = await loadPaymentOrdersWithProp('taxItems', true)
        const paymentOrders = paymentOrdersWithTaxItems.filter(paymentOrder => paymentOrder.taxItems.some(taxItem => taxItem.taxCode === tax?.code))

        const { loadTicketsWithProp } = ticketLoaders()
        const ticketsWithTaxItems = await loadTicketsWithProp('taxItems', true)
        const tickets = ticketsWithTaxItems.filter(ticket => ticket.taxItems.some(taxItem => taxItem.taxCode === tax?.code))

        return paymentOrders.length === 0 &&
            tickets.length === 0 &&
            productPricesCount === 0
    }

    const searchTaxes = (
        taxList: Tax[],
        text = '',
        showAll = false
    ): Tax[] => {
        const valuesFn = (taxDT: DataType) => {
            const { name, type, code, legalCode } = taxDT as Tax
            return [
                normalize(name),
                normalize(Labels.taxTypeLabel(type)),
                normalize(code),
                normalize(legalCode || '')
            ]
        }
        const sortFn = (taxDT_A: DataType, taxDT_B: DataType) => 
            (taxDT_A as Tax).name.localeCompare((taxDT_B as Tax).name)

        return search(taxList, valuesFn, sortFn, text, showAll) as Tax[]
    }

    return {
        getTotalSaleTax,
        getVat21Tax,
        calculateTaxBaseAmount,
        calculateTaxAmount,
        canRemoveTax,
        searchTaxes
    }
}
